import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { Card } from '../components/ui/card';
import { StatStrip } from '../components/StatStrip';
import { Input } from '../components/ui/input';
import { RefreshCw, Search, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LoadError } from '../components/LoadError';
import { useTableControls, SortHeader, TablePager } from '../components/TableControls';
import { AGING_CLASS, OUT_OF_STOCK, StockStatusBadge, StockLegend, formatDaysOfStock, daysOfStockTitle, daysSortValue } from '../lib/stockStatus';
import { StatusBadge } from '../components/StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEARCH_KEYS = ['sku_code', 'product_name'];
const INITIAL_SORT = { key: 'days_of_stock', dir: 'desc' };
const SORT_VALUE = {
  days_of_stock: (r) => daysSortValue(r.days_of_stock, r.available_stock, r.daily_sales),
  classification: (r) => Object.keys(AGING_CLASS).indexOf(r.classification),
  has_open_po: (r) => (r.has_open_po ? 1 : 0),
};
const CHANNEL_LABELS = { BOTH: 'Seller + Retail', SELLER_ONLY: 'Seller only', RETAIL_ONLY: 'Retail only' };

// Count colour per aging class (700 shades for contrast on white).
const AGING_TONE = { Active: 'green', Slow: 'blue', 'Very Slow': 'amber', Critical: 'orange', Dead: 'gray' };

export const StockHealthPage = () => {
  const { country } = useCountry();
  const [healthData, setHealthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('ALL');

  const fetchHealthData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/stock-health?country=${country}`, { headers: getAuthHeaders() });
      setHealthData(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  const filteredData = useMemo(
    () => (filter === 'ALL' ? healthData : healthData.filter(item => item.classification === filter)),
    [filter, healthData],
  );
  const table = useTableControls(filteredData, { searchKeys: SEARCH_KEYS, initialSort: INITIAL_SORT, pageSize: 50, sortValue: SORT_VALUE });
  const { setPage } = table;
  // A new aging-class filter is a new list: start it on page 1.
  useEffect(() => { setPage(1); }, [filter, setPage]);

  const stats = useMemo(() => {
    const counts = Object.fromEntries(Object.keys(AGING_CLASS).map(k => [k, 0]));
    healthData.forEach(i => { if (counts[i.classification] != null) counts[i.classification] += 1; });
    return counts;
  }, [healthData]);

  return (
    <div className="space-y-6" data-testid="stock-health-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Stock Health</h1>
          <p className="mt-2 text-muted-foreground">Inventory aging: how many days the current stock will last at the last 30 days' sales</p>
        </div>
        <Button onClick={fetchHealthData} disabled={loading} data-testid="refresh-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <StatStrip testId="aging-summary" items={Object.entries(AGING_CLASS).map(([key, def]) => ({
        key: key.replace(/\s+/g, '-').toLowerCase(), label: def.label, hint: def.rule,
        value: loading || error ? '–' : stats[key], tone: AGING_TONE[key] || 'gray',
        active: filter === key, onClick: () => setFilter(filter === key ? 'ALL' : key),
      }))} />

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden="true" />
            <Input
              value={table.query}
              onChange={(e) => table.setQuery(e.target.value)}
              placeholder="Search SKU or product..."
              aria-label="Search SKU or product"
              className="pl-9"
              data-testid="stock-health-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="filter-select" aria-label="Aging class">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All aging classes</SelectItem>
              {Object.entries(AGING_CLASS).map(([key, def]) => (
                <SelectItem key={key} value={key}>{def.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <StockLegend map={AGING_CLASS} title="What do these mean?" className="basis-full" testId="stock-health-legend" />
        </div>

        {error ? (
          <LoadError what="stock health" error={error} onRetry={fetchHealthData} retrying={loading} testId="stock-health-error" />
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-600" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading stock health…
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stock-health-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <SortHeader label="SKU Code" sortKey="sku_code" table={table} className="p-4" />
                    <SortHeader label="Product Name" sortKey="product_name" table={table} className="p-4" />
                    <SortHeader label="Available (units)" sortKey="available_stock" table={table} align="right" className="p-4" />
                    <SortHeader label="Days of stock" sortKey="days_of_stock" table={table} align="right" className="p-4" />
                    <SortHeader label="Aging" sortKey="classification" table={table} className="p-4" />
                    <SortHeader label="Channel" sortKey="channel_mode" table={table} className="p-4" />
                    <SortHeader label="Open PO" sortKey="has_open_po" table={table} className="p-4" />
                  </tr>
                </thead>
                <tbody>
                  {table.pageRows.map((item, index) => (
                    <tr key={item.sku_code} className="border-b hover:bg-muted/50 transition-colors" data-testid={`health-row-${index}`}>
                      <td className="p-4 font-mono font-medium">{item.sku_code}</td>
                      <td className="p-4">{item.product_name}</td>
                      <td className={`p-4 text-right font-mono ${item.available_stock <= 0 ? 'text-red-700 font-semibold' : ''}`}>
                        {Number(item.available_stock || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-4 text-right font-mono whitespace-nowrap" title={daysOfStockTitle(item.days_of_stock, { available: item.available_stock, velocity: item.daily_sales })}>
                        {formatDaysOfStock(item.days_of_stock, { available: item.available_stock, velocity: item.daily_sales })}
                      </td>
                      <td className="p-4">
                        {Number(item.available_stock) <= 0
                          ? <StatusBadge status="OUT_OF_STOCK" label={OUT_OF_STOCK.label} tone={OUT_OF_STOCK.tone} title={`${OUT_OF_STOCK.rule} (aging class: ${AGING_CLASS[item.classification]?.label || item.classification})`} />
                          : <StockStatusBadge status={item.classification} map={AGING_CLASS} />}
                      </td>
                      <td className="p-4 text-xs text-gray-700">{CHANNEL_LABELS[item.channel_mode] || item.channel_mode}</td>
                      <td className="p-4">
                        {item.has_open_po
                          ? <span className="font-medium text-green-700">Yes</span>
                          : <span className="text-gray-500">No</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {table.total === 0 && (
                <div className="text-center py-12 text-muted-foreground" data-testid="stock-health-empty">
                  {healthData.length === 0
                    ? 'No SKUs found. Add SKUs and stock to see their aging here.'
                    : 'No SKUs match your search or filter.'}
                </div>
              )}
            </div>
            <TablePager table={table} itemLabel="SKUs" />
          </>
        )}
      </Card>
    </div>
  );
};
